import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  Droplets,
  FlaskConical,
  ArrowUpRight,
} from 'lucide-react';

interface HydroStep {
  id: string;
  number: string;
  title: string;
  description: string;
  icon: React.ElementType;
}

const stepsData: HydroStep[] = [
  {
    id: 'step-1',
    number: '01',
    title: 'hydroponics_step_1_title',
    description: 'hydroponics_step_1_desc',
    icon: Droplets,
  },
  {
    id: 'step-2',
    number: '02',
    title: 'hydroponics_step_2_title',
    description: 'hydroponics_step_2_desc',
    icon: FlaskConical,
  },
];

// Real hydroponic greenhouse photograph used as the full-bleed backdrop.
const BACKGROUND_IMAGE_URL =
  '/h2.webp';

export const Hydroponics: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState<number>(0);

  const currentStep = stepsData[activeStep];
  const nextStepIndex = (activeStep + 1) % stepsData.length;
  const nextStep = stepsData[nextStepIndex];

  return (
    <section className="relative w-full min-h-screen py-10 px-4 md:px-10 overflow-hidden ">
      {/* Full-screen realistic hydroponics background photo */}
      <div
        className="absolute inset-0 bg-cover bg-center "
        style={{ backgroundImage: `url(${BACKGROUND_IMAGE_URL})` }}
        aria-hidden="true"
      />
      {/* Darkening/legibility overlay so glass cards & text stay readable over the photo */}
      <div
        className="absolute inset-0 bg-gradient-to-b from-[#00110f]/70 via-[#00110f]/50 to-[#00110f]/80"
        aria-hidden="true"
      />

      <div className="relative z-10 max-w-7xl mx-auto p-6 border border-white/25 bg-white/10 backdrop-blur-xs shadow-lg p-5 md:p-4 rounded-xl">
        <div className="text-center mb-0">

          <h2 className="text-2xl sm:text-4xl lg:text-5xl font-extrabold text-white drop-shadow-[0_2px_12px_rgba(0,0,0,0.45)]">
            {t('hydroponics_home_heading')}
          </h2>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-center ">

          {/* Left Side Column: Active Step Details (glassmorphism card) */}
          <div className="lg:col-span-3 order-1 lg:order-1 flex flex-col items-start space-y-3 lg:-translate-y-8 lg:translate-x-12 rounded-3xl ">
            <div className="hidden lg:flex items-center w-full gap-4 mb-1">
              <div className="relative flex items-center justify-center w-24 h-16">
                <img
                  src="/shape-16.webp"
                  loading="lazy"
                  alt="Wheat Wreath Accent"
                  className="absolute inset-0 w-full h-full object-contain pointer-events-none"
                />
                <div className="z-10 w-8 h-8 bg-[#7EDE56] rounded-full flex items-center justify-center text-[#002F3F] font-bold text-xs shadow-sm">
                  {currentStep.number}
                </div>
              </div>
              <div className="w-56 border-b-2 border-dotted border-white/40" />
            </div>

            <div className="lg:hidden relative flex items-center justify-center w-24 h-16">
              <img
                src="/shape-16.webp"
                  loading="lazy"
                alt="Wheat Wreath Accent"
                className="absolute inset-0 w-full h-full object-contain pointer-events-none"
              />
              <div className="z-10 w-8 h-8 bg-[#8cc63f] rounded-full flex items-center justify-center text-[#002F3F] font-bold text-xs shadow-sm">
                {currentStep.number}
              </div>
            </div>

            <h3 className="text-lg font-bold text-white leading-snug drop-shadow-sm">
              {t(currentStep.title)}
            </h3>
            <p className="text-white/85 text-xs leading-relaxed max-w-xs">
              {t(currentStep.description)}
            </p>

            <button
              type="button"
              onClick={() => navigate('/hydroponics')}
              className="mt-3 inline-flex items-center gap-2 bg-[#7EDE56] text-[#002F3F] text-sm px-5 py-2.5 rounded-xl transition-all shadow-sm hover:bg-[#6bcf45]"
            >
              <span>{t('hydroponics_more_details')}</span>
              <ArrowUpRight className="w-4 h-4" />
            </button>
          </div>

          {/* Center Column: Pinwheel with icons + center leaf — no background, kept transparent */}
          <div className="lg:col-span-6 order-2 lg:order-2 flex justify-center items-center my-4 lg:my-0">
            <div className="relative w-full max-w-[420px] min-h-[320px] sm:max-w-[440px] lg:w-[480px] lg:h-[400px] xl:w-[450px] xl:h-[500px] flex items-center justify-center">
              {/* Background Pinwheel Shape */}
              <img

                src="/w.webp"
                  loading="lazy"
                alt="Hydroponics Pinwheel Shape"
                className="w-full h-full object-contain animate-[spin_30s_linear_infinite]" />
            </div>
          </div>

          {/* Mobile Next Step Preview (glassmorphism card) */}
          <div className="lg:hidden order-3 w-full rounded-3xl ">
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <div className="relative flex items-center justify-center w-14 h-14">
                  <img
                    src="/shape-16.webp"
                  loading="lazy"
                    alt="Wheat Wreath Accent"
                    className="absolute inset-0 w-full h-full object-contain pointer-events-none"
                  />
                  <div className="z-10 w-8 h-8 bg-[#7EDE56] rounded-full flex items-center justify-center text-[#002F3F] font-bold text-xs shadow-sm">
                    {nextStep.number}
                  </div>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-widest text-white/70">{t('hydroponics_next_step_label')}</p>
                  <h4 className="text-sm font-semibold text-white">{t(nextStep.title)}</h4>
                </div>
              </div>
             
            </div>
            <p className="text-white/75 text-xs mt-3 line-clamp-2">{t(nextStep.description)}</p>
          </div>

          {/* Right Side Column: Next Step Preview (glassmorphism card) */}
          <div className="lg:col-span-3 order-4 hidden lg:flex flex-col items-end text-right space-y-3 lg:translate-y-30 lg:-translate-x-12 rounded-3xl ">
            <div className="hidden lg:flex items-center w-full gap-4 -mt-2 mb-1 justify-end">
              <div className="w-56 border-b-2 border-dotted border-white/40" />
              <div className="relative flex items-center justify-center w-24 h-16">
                <img
                  src="/shape-16.webp"
                  loading="lazy"
                  alt="Wheat Wreath Accent"
                  className="absolute inset-0 w-full h-full object-contain pointer-events-none"
                />
                <div className="z-10 w-8 h-8 bg-[#7EDE56] rounded-full flex items-center justify-center text-[#002F3F] font-bold text-xs shadow-sm">
                  {nextStep.number}
                </div>
              </div>
            </div>

            <div onClick={() => setActiveStep(nextStepIndex)} className="cursor-pointer group">
              <h4 className="text-lg font-bold text-white">
                {t(nextStep.title)}
              </h4>
              <p className="text-white/75 text-xs mt-1 max-w-xs line-clamp-2">
                {t(nextStep.description)}
              </p>
            </div>
          </div>

        </div>
      </div>
    </section>
  );
};

export default Hydroponics;
